<template>
  <div class="ak-score" :size="size" :class="classes">
    <div class="ak-score__figure">
      <ak-tooltip v-if="displayScore > 999 && size !== 'none' && size !== 'tiny'">
        <template #trigger>
          <ak-icon type="fireball" class="inline-block" />
        </template>
        {{ displayScore }}
      </ak-tooltip>
      <div v-if="displayScore <= 999 || size === 'none' || size === 'tiny'">{{ displayScore }}</div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-score :score="50"></ak-score>
   */
  name: 'AkScore',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Number}
     * @description The score of the contact/lead
     */
    score: {
      type: Number,
      default: 0,
    },

    /**
     * @type {String}
     * @description Controls the size of the score badge
     */
    size: {
      default: "normal",
      type: String,
    },

    /**
     * @type {String}
     * @description Custom score alternative to actual score
     */
    customScore: {
      type: String,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Flag whether to use custom score or not
     */
     customScoreFlag: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Object}
     * @description Account Heat defaults
     */
     heat: {
      type: Object,
      default: {
        cold: {
          min: 0,
          max: 40,
        },
        tepid: {
          min: 41,
          max: 84,
        },
        warm: {
          min: 85,
          max: 125,
        },
        hot: {
          min: 125,
          max: 9999,
        },
      },
    },

    /**
     * @type {Boolean}
     * @description Flag whether score has an avatar next to it or not
     */
     hasAvatar: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const displayScore = ref(0)

    const classes = computed(() => {
      const res = [
        `ak-score--${props.size}`
      ] 

      if (props.hasAvatar) res.push('ak-score--has-avatar')

      // if customScore is enabled, display score is the custom one
      if (props.customScoreFlag) {
        displayScore.value = props.customScore || 0
      } else {
        displayScore.value = props.score || 0
      }

      if (displayScore.value < 0) {
        displayScore.value = 0
      }

      if (displayScore.value >= props.heat.hot.min) {
        res.push('ak-score--hot')
      } else if (displayScore.value >= props.heat.warm.min) {
        res.push('ak-score--warm')
      } else if (displayScore.value >= props.heat.tepid.min) {
        res.push('ak-score--tepid')
      } else {
        res.push('ak-score--cold')
      }

      return res
    })
  
    return {
      classes,
      displayScore,
    }
  },
})
</script>

<style lang="scss" rel="stylesheet/scss">
@import '../../scss/_settings.scss';

$badge-padding-vert: .225em;
$badge-padding-horz: .667em;

.ak-score {
  display: inline-block;
  border-radius: 50%;
  &--cold   {
    .ak-score__figure {
      color: $score-cold;
    }
    &.ak-score--tiny {
      .ak-score__figure {
        background-color: $score-cold;
      }
    }
  }
  &--tepid  {
    .ak-score__figure {
      color: $score-tepid;
    }
    &.ak-score--tiny {
      .ak-score__figure {
        background-color: $score-tepid;
      }
    }
  }
  &--warm   {
    .ak-score__figure {
      color: $score-warm;
    }
    &.ak-score--tiny {
      .ak-score__figure {
        background-color: $score-warm;
      }
    }
  }
  &--hot    {
    .ak-score__figure {
      color: $score-hot;
    }
    &.ak-score--tiny {
      .ak-score__figure {
        background-color: $score-hot;
      }
    }
  }

  &--has-avatar {
    margin-right: -10px;
    position: relative;
    z-index: 2;
    vertical-align: middle;
  }

  &__figure {
    width: 40px;
    height: 40px;
    font-weight: $font-xbold;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    border-radius: 50%;
    border: 3px solid;
    background-color: #fff;
  }

  &--none { 
    .ak-score__figure {
      width: auto;
      height: auto;
      line-height: inherit;
      border: none;
      border-radius: none;
      background-color: transparent;
    }
  }
  &--tiny { 
    .ak-score__figure {
      width: auto;
      height: auto;
      padding: threequarts($badge-padding-vert) $badge-padding-horz $badge-padding-vert;
      font-size: .75em;
      line-height: $base-line-height;
      border: none;
      color: #fff;
      border-radius: 5em;
    }
    &.ak-score--has-avatar {
      margin-right: -5px;
    }
  }
  &--small { 
    .ak-score__figure {
      width: 2.25rem;
      height: 2.25rem;
    }
    &.ak-score--has-avatar {
      margin-right: -10px;
    }
  }
  &--large { 
    .ak-score__figure {
      width: 4rem;
      height: 4rem;
      font-size: 1.75rem;
      border-width: 5px;
    }
    &.ak-score--has-avatar {
      margin-right: -20px;
    }
  }
}

.bg {
  &-cold   {
    background-color: $score-cold;
  }
  &-tepid  {
    background-color: $score-tepid;
  }
  &-warm   {
    background-color: $score-warm;
  }
  &-hot    {
    background-color: $score-hot;
  }
}
</style>
